/**
 * Shared utilities + small UI primitives for the Workspace Overview /
 * Portfolio module.
 *
 * v260.76 — extracted from WorkspaceOverviewPage.js as a pure
 * mechanical refactor to shrink the parent monolith from ~2042 lines.
 * No behaviour change. Re-imports the same React/UI deps the parent
 * already pulled in.
 */
import React from 'react';
import { Card, CardContent } from '../../components/ui/card';

const DEV_TYPE = {
  new_dev: { label: 'New Development', color: '#8B5CF6' },
  integration: { label: 'Integration', color: '#10B981' },
  migration: { label: 'Data Migration', color: '#F59E0B' },
};

// v261 — Quick-search highlighting: wraps every case-insensitive match of
// `query` inside `text` in a glowing <mark> so the eye lands on it.
function HighlightMatch({ text, query }) {
  const t = text || '';
  const q = (query || '').trim();
  if (!q) return t;
  const lower = t.toLowerCase();
  const ql = q.toLowerCase();
  let idx = lower.indexOf(ql);
  if (idx === -1) return t;
  const parts = [];
  let i = 0;
  while (idx !== -1) {
    if (idx > i) parts.push(t.slice(i, idx));
    parts.push(
      <mark
        key={idx}
        className="bg-amber-200/90 text-[#0F2040] rounded-[2px] px-px font-semibold shadow-[0_0_8px_rgba(245,158,11,0.65)]"
      >
        {t.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
    idx = lower.indexOf(ql, i);
  }
  if (i < t.length) parts.push(t.slice(i));
  return <>{parts}</>;
}

// v260.136 — Public-Cloud release marker palette, mirrored from the
// Planner Gantt so releases read with the same colour language across
// the app (prod = red, test = blue, maintenance = purple).
const RELEASE_COLORS = {
  prod: '#DC2626',
  test: '#2563EB',
  maintenance: '#9333EA',
};

const RELEASE_TYPE_LABEL = {
  prod: 'Full Release',
  test: 'Test Release',
  maintenance: 'Maintenance',
};

// Map each release event onto the week-column index whose week contains
// the release date (the last column whose ISO start ≤ the release date).
// Releases before the visible window are dropped. weekLabels are ascending
// ISO date strings so lexicographic comparison matches chronological order.
function buildReleaseCols(weekLabels, releases) {
  if (!weekLabels?.length || !releases?.length) return [];
  const cols = [];
  for (const r of releases) {
    if (!r?.date) continue;
    let idx = -1;
    for (let i = 0; i < weekLabels.length; i++) {
      if (weekLabels[i] <= r.date) idx = i; else break;
    }
    if (idx < 0) continue; // release falls before the window start
    cols.push({ ...r, idx });
  }
  return cols;
}

// { columnIndex → [release, …] } for fast per-cell lookup in row renderers.
function releaseColsByIdx(releaseCols) {
  const m = {};
  for (const rc of releaseCols || []) {
    (m[rc.idx] = m[rc.idx] || []).push(rc);
  }
  return m;
}

// Distinct anchored-release labels for a project, derived from its dev
// phases (each band may carry an `anchor_release_label`). Used by the
// portfolio CSVs to show which releases a project's deliverables ship in.
function anchoredReleasesForProject(p) {
  const seen = new Set();
  for (const ph of (p?.dev_phases || [])) {
    if (ph.anchor_release_label) seen.add(ph.anchor_release_label);
  }
  return Array.from(seen);
}

// v260.137 — Deliverables that finish after their anchored release date
// (schedule risk). Each phase carries `release_at_risk` + `release_slip_days`.
function atRiskPhasesForProject(p) {
  return (p?.dev_phases || []).filter(ph => ph.release_at_risk);
}

function offsetDate(weeks) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return '';
  // Dates are stored as UTC date-only strings. Render them in UTC too so
  // users behind UTC (e.g. the Americas) don't see them shift back a day.
  return new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

function dayDiff(a, b) {
  return Math.floor((new Date(b + 'T00:00:00Z') - new Date(a + 'T00:00:00Z')) / 86400000);
}

function csvEscape(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// Build the row matrix for the portfolio-capacity CSV export. Layout:
//
//   # Seertech Blueprint Studio — Portfolio Capacity
//   # Date range / Granularity / FTE basis metadata
//
//   (blank)
//   Portfolio totals by {granularity}
//   <granularity>,2026-04-25,2026-05-02,…,Peak FTE,Peak week
//   Consulting,0.8,1.0,…,5.3,2026-05-30
//   …
//
//   (blank)
//   Per-project breakdown
//   Project,Client,Status,Start,End,Go-Live,<granularity>,2026-04-25,…,Total hrs
//   …
//
// All FTE cells render as 1-decimal numbers (1.0 = one full-time person).
function buildCapacityCsvRows(data, granularity) {
  const rows = [];
  const weekLabels = data?.date_range?.week_labels || [];
  const fteBasis = data?.fte_hours_per_week || 40;
  const granLabel = granularity === 'role' ? 'Role' : 'Discipline';

  rows.push(['Seertech Blueprint Studio — Portfolio Capacity']);
  rows.push([`Date range`, data?.date_range?.start || '', '→', data?.date_range?.end || '']);
  rows.push([`Granularity`, granLabel]);
  rows.push([`FTE basis`, `${fteBasis}h/wk = 1.0 FTE`]);
  rows.push([`Projects in range`, data?.project_count || 0]);
  rows.push([]);

  // ── Portfolio totals ──────────────────────────────────────────────
  rows.push([`Portfolio totals by ${granLabel.toLowerCase()}`]);
  const totalsHeader = [granLabel, ...weekLabels, 'Peak FTE', 'Peak week'];
  rows.push(totalsHeader);
  const totals = granularity === 'role'
    ? (data?.totals?.per_role || []).map(r => ({ label: r.role_label || r.role, pct: r.weekly_fte_pct || [] }))
    : (data?.totals?.per_discipline || []).map(d => ({ label: d.discipline, pct: d.weekly_fte_pct || [] }));
  for (const row of totals) {
    let peakIdx = -1, peakPct = -1;
    row.pct.forEach((v, i) => { if ((v || 0) > peakPct) { peakPct = v || 0; peakIdx = i; } });
    const wkCells = row.pct.map(v => ((v || 0) / 100).toFixed(1));
    rows.push([
      row.label,
      ...wkCells,
      (peakPct / 100).toFixed(1),
      peakIdx >= 0 ? (weekLabels[peakIdx] || '') : '',
    ]);
  }

  rows.push([]);

  // ── Per-project breakdown ────────────────────────────────────────
  rows.push(['Per-project breakdown']);
  const projHeader = ['Project', 'Client', 'Status', 'Start', 'End', 'Go-Live', 'Anchored Releases', 'Release Risk', granLabel, ...weekLabels, 'Total hrs'];
  rows.push(projHeader);
  for (const p of (data?.projects || [])) {
    const anchoredReleases = anchoredReleasesForProject(p).join(' · ');
    const atRiskCount = atRiskPhasesForProject(p).length;
    const releaseRisk = atRiskCount > 0 ? `${atRiskCount} at risk` : '';
    const ra = p.role_allocation || [];
    const rolled = granularity === 'role'
      ? ra.map(r => ({ label: r.role_label || r.role, pct: r.weekly_fte_pct || [] }))
      : Object.values(ra.reduce((acc, r) => {
          const key = r.discipline || 'Other';
          acc[key] = acc[key] || { label: key, pct: [] };
          (r.weekly_fte_pct || []).forEach((v, i) => {
            acc[key].pct[i] = Math.round(((acc[key].pct[i] || 0) + (v || 0)) * 10) / 10;
          });
          return acc;
        }, {}));
    for (const row of rolled) {
      const wkCells = row.pct.map(v => ((v || 0) / 100).toFixed(1));
      rows.push([
        p.name || '',
        p.client_name || '',
        p.status || '',
        p.start_date || '',
        p.end_date || '',
        p.go_live_date || '',
        anchoredReleases,
        releaseRisk,
        row.label,
        ...wkCells,
        p.total_hours || 0,
      ]);
    }
  }

  return rows;
}


function buildDevPlanCsvRows(data) {
  const rows = [];
  const projects = (data?.projects || []).filter(p => (p.dev_phases || []).length > 0);

  // Portfolio totals header — mirrors what the legend chips show in-app
  // so the CSV is self-describing without needing the user to remember
  // what window they exported.
  const totals = projects.reduce((acc, p) => {
    for (const ph of (p.dev_phases || [])) {
      const t = ph.type || 'other';
      acc[t] = (acc[t] || 0) + (ph.hours || 0);
    }
    return acc;
  }, {});
  const grandTotal = Object.values(totals).reduce((s, v) => s + v, 0);

  rows.push(['# Seertech Blueprint Studio — Development Plan']);
  rows.push([`# Date range: ${data?.date_range?.start || ''} → ${data?.date_range?.end || ''}`]);
  rows.push([`# Projects in scope: ${projects.length}`]);
  rows.push([`# New Development: ${(totals.new_dev || 0).toFixed(1)} h · Integration: ${(totals.integration || 0).toFixed(1)} h · Data Migration: ${(totals.migration || 0).toFixed(1)} h · TOTAL: ${grandTotal.toFixed(1)} h`]);
  rows.push([]);

  rows.push(['Project', 'Type', 'Item', 'Group', 'Start', 'End', 'Hours', 'Anchored Release', 'Release Slip (days)']);
  const TYPE_LABEL = { new_dev: 'New Development', integration: 'Integration', migration: 'Data Migration', other: 'Other' };
  for (const p of projects) {
    // Stable sort: by type (canonical order) then start_date.
    const ordered = [...(p.dev_phases || [])].sort((a, b) => {
      const order = { new_dev: 0, integration: 1, migration: 2, other: 3 };
      const da = order[a.type] ?? 9;
      const db = order[b.type] ?? 9;
      if (da !== db) return da - db;
      return String(a.start_date || '').localeCompare(String(b.start_date || ''));
    });
    for (const ph of ordered) {
      const rel = ph.anchor_release_label
        ? (ph.anchor_release_date ? `${ph.anchor_release_label} (${ph.anchor_release_date})` : ph.anchor_release_label)
        : '';
      const slip = ph.release_at_risk ? String(ph.release_slip_days || '') : '';
      rows.push([
        p.name || '',
        TYPE_LABEL[ph.type] || ph.type || '',
        ph.label || '',
        ph.group || '',
        ph.start_date || '',
        ph.end_date || '',
        (ph.hours || 0).toFixed(1),
        rel,
        slip,
      ]);
    }
  }
  return rows;
}


// ─── Project View ─────────────────────────────────────────────────────

// Auto-save status badge — lives where the "Save as default view" button
// used to. Idle: shows a calm "Auto-saved" label so users trust changes
// are persisted. Saving: pulses an amber dot. Saved: flashes emerald for
// ~1.8s after each successful PUT before fading back to idle.

function WeekHeader({ weekLabels, todayIdx, firstColLabel, firstColWidth, pulseWeekIdx = -1, cellWidth = 18, releaseCols = [] }) {
  const relByIdx = releaseColsByIdx(releaseCols);
  return (
    <thead>
      <tr className="bg-slate-50 border-b border-slate-200">
        <th className="text-[10px] uppercase tracking-wide text-slate-500 px-3 py-2 text-left sticky left-0 bg-slate-50 border-r border-slate-200" style={{ width: firstColWidth, minWidth: firstColWidth }}>{firstColLabel}</th>
        {weekLabels.map((w, i) => {
          const month = new Date(w + 'T00:00:00Z').getUTCMonth();
          const prevMonth = i > 0 ? new Date(weekLabels[i - 1] + 'T00:00:00Z').getUTCMonth() : -1;
          const isMonthStart = month !== prevMonth;
          const isPulse = i === pulseWeekIdx;
          return (
            <th key={w}
              data-week-idx={i}
              className={`text-[9px] text-slate-400 font-normal py-1 ${i === todayIdx ? 'bg-amber-100 text-amber-900 font-semibold' : ''} ${isMonthStart ? 'border-l border-slate-200' : ''} ${isPulse ? 'bg-amber-200 ring-2 ring-amber-400 animate-pulse' : ''}`}
              style={{ width: cellWidth }}
              title={w}>
              {isMonthStart ? new Date(w + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }) : ''}
            </th>
          );
        })}
      </tr>
      {/* v260.136 — Public-Cloud release lane. Mirrors the Planner's
          release markers so leadership can see WHEN releases occur in
          relation to each project's deliverables below. A coloured dot
          sits in the week that contains the release; hover reveals the
          label, type and date. */}
      {releaseCols.length > 0 && (
        <tr className="bg-white border-b border-slate-200" data-testid="overview-release-lane">
          <th
            className="text-[9px] uppercase tracking-wide text-slate-400 px-3 py-1 text-left sticky left-0 bg-white border-r border-slate-200"
            style={{ width: firstColWidth, minWidth: firstColWidth }}
          >
            Releases
          </th>
          {weekLabels.map((w, i) => {
            const rs = relByIdx[i];
            return (
              <th key={w} className="relative p-0" style={{ width: cellWidth, height: 16 }}>
                {rs && rs.map((r, k) => (
                  <span
                    key={r.id || `${i}-${k}`}
                    className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-full ring-1 ring-white shadow-sm"
                    style={{ top: 3 + k * 7, background: RELEASE_COLORS[r.release_type] || '#64748B' }}
                    title={`${r.label || RELEASE_TYPE_LABEL[r.release_type] || 'Release'}${r.date ? ` · ${r.date}` : ''}`}
                    data-testid={`overview-release-marker-${i}`}
                  />
                ))}
              </th>
            );
          })}
        </tr>
      )}
    </thead>
  );
}

function EmptyState({ message = 'No projects match the current filter / date range.' }) {
  return (
    <Card className="mt-2"><CardContent className="p-12 text-center text-sm text-slate-500" data-testid="overview-empty">
      <img src="/seertech-mark.png" alt="" aria-hidden className="w-12 h-12 mx-auto opacity-20 mb-3" />
      {message}
    </CardContent></Card>
  );
}

export {
  DEV_TYPE,
  RELEASE_COLORS,
  RELEASE_TYPE_LABEL,
  buildReleaseCols,
  releaseColsByIdx,
  anchoredReleasesForProject,
  atRiskPhasesForProject,
  offsetDate,
  fmtDate,
  dayDiff,
  csvEscape,
  buildCapacityCsvRows,
  buildDevPlanCsvRows,
  WeekHeader,
  EmptyState,
  HighlightMatch,
};
